import React, { useState, useCallback, useRef } from 'react';
import { SyutSelect, type SelectOption } from 'components/ui';
import notificationService from 'services/notificationService';
import { useAuthContext } from 'contexts/AuthContext';
import { itemService, stockLocationService } from 'api/services';
import type { ItemDto, ItemType, DocumentLineLedgerDto } from 'api/types';

// ---- Item lines on commercial documents (ADR-0058) ----
// Built for purchases (slice 3) and shared with sales: the item half of an entry row, its
// lookups (item / pack / location), the line's detail strip and its read view. Which side of
// the trade a document is on decides the items offered, the default pack and the locations.

const money = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const qtyFmt = (n: number) => (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 6 });
export const itemLabel = (code?: string | null, name?: string | null) => code ? `${code} — ${name || ''}` : (name || '');
const packLabel = (u: { uomCode?: string | null; factorToBase: number }) => `${u.uomCode || '?'} (×${qtyFmt(u.factorToBase)})`;

/**
 * Which document an item line sits on:
 * - PURCHASE: items not blocked for purchase; purchase default pack; storage locations.
 * - SALE: items not blocked for sale; sales default pack; saleable locations only.
 * - SALES_RETURN: items not blocked for sale; sales default pack; any storage location
 *   (a return may land in a damaged-goods store).
 * Locations are always the current business unit's and never in-transit.
 */
export type ItemLineSide = 'PURCHASE' | 'SALE' | 'SALES_RETURN';

/** The item half of an entry row (no line type — each document has its own). Label caches ride along so a restored draft renders without a fetch. */
export interface ItemFields {
  itemId: string;
  itemText: string;
  itemName: string;
  /** '' until known — a saved line carries no item type (a location means stock). */
  itemType: ItemType | '';
  uomId: string;
  uomCode: string;
  factorToBase: number;
  baseUomCode: string;
  stockLocationId: string;
  locationText: string;
}
export const blankItemFieldValues = (): ItemFields => ({
  itemId: '', itemText: '', itemName: '', itemType: '',
  uomId: '', uomCode: '', factorToBase: 1, baseUomCode: '', stockLocationId: '', locationText: '',
});
/** A saved (or prefilled) line's item half. */
export const itemFieldValuesOf = (l: {
  itemId?: string | null; itemCode?: string | null; itemName?: string | null;
  uomId?: string | null; uomCode?: string | null; factorToBase?: number | null;
  stockLocationId?: string | null; locationCode?: string | null; locationName?: string | null;
}): ItemFields => ({
  itemId: l.itemId || '', itemText: itemLabel(l.itemCode, l.itemName), itemName: l.itemName || '',
  itemType: l.stockLocationId ? 'STOCK' : '',
  uomId: l.uomId || '', uomCode: l.uomCode || '', factorToBase: l.factorToBase || 1, baseUomCode: '',
  stockLocationId: l.stockLocationId || '', locationText: itemLabel(l.locationCode, l.locationName),
});

/** Default pack of a picked item: its purchase / sales default, else the base unit. */
const defaultPack = (item: ItemDto, side: ItemLineSide) =>
  item.uoms.find(u => (side === 'PURCHASE' ? u.isPurchaseDefault : u.isSalesDefault))
  ?? item.uoms.find(u => u.isBase)
  ?? item.uoms.find(u => u.uomId === item.baseUomId)
  ?? item.uoms[0];

/**
 * Item / pack / location lookups for a document's item lines. Where each item line posts comes
 * from the document's line-ledgers read (usePurchaseLineLedgers / useSalesLineLedgers), not from here.
 */
export const useItemLineLookups = (side: ItemLineSide) => {
  const [itemDetails, setItemDetails] = useState<Record<string, ItemDto>>({});
  const itemDetailsRef = useRef(itemDetails); itemDetailsRef.current = itemDetails;
  const listedItems = useRef<Map<string, ItemDto>>(new Map());
  const isPurchase = side === 'PURCHASE';

  const loadItems = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await itemService.getAll(isPurchase
        ? { searchTerm: input || undefined, pageSize: 30, blockedForPurchase: false }
        : { searchTerm: input || undefined, pageSize: 30, blockedForSale: false });
      return res.data.filter(it => !(isPurchase ? it.blockedForPurchase : it.blockedForSale)).map(it => {
        listedItems.current.set(it.id, it);
        return { value: it.id, label: itemLabel(it.itemCode, it.itemName) };
      });
    } catch { return []; }
  }, [isPurchase]);

  // The goods leg posts in the document's BU, so only that BU's locations are offered (ADR-0058 §8).
  const { selectedBusinessUnit } = useAuthContext();
  const buId = selectedBusinessUnit?.id;
  const saleableOnly = side === 'SALE';
  const loadLocations = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await stockLocationService.lookup({
        searchTerm: input || undefined, pageSize: 50, locationType: 'STORAGE', businessUnitId: buId,
        ...(saleableOnly ? { saleableOnly: true } : {}),
      });
      return res.data.filter(loc => loc.locationType !== 'IN_TRANSIT' && (!saleableOnly || loc.isSaleable !== false))
        .map(loc => ({ value: loc.id, label: itemLabel(loc.locationCode, loc.locationName) }));
    } catch { return []; }
  }, [buId, saleableOnly]);

  /** Best-effort detail reads for items already on the form (edit / copy / prefill) — packs for the Pack picker. */
  const loadItemDetails = useCallback(async (ids: string[]) => {
    const missing = Array.from(new Set(ids.filter(id => id && !itemDetailsRef.current[id])));
    if (!missing.length) return;
    const results = await Promise.all(missing.map(id => itemService.getById(id).catch(() => null)));
    setItemDetails(prev => {
      const next = { ...prev };
      results.forEach(it => { if (it) next[it.id] = it; });
      return next;
    });
  }, []);

  /** Item picked: the row resets its pack at once; the item's read then fills the default pack and (stock) default location. */
  const pickItem = useCallback(async <R extends ItemFields & { key: number }>(
    key: number, itemId: string, setRows: React.Dispatch<React.SetStateAction<R[]>>,
  ) => {
    const listed = itemId ? (itemDetailsRef.current[itemId] ?? listedItems.current.get(itemId)) : undefined;
    setRows(rows => rows.map(r => r.key !== key ? r : {
      ...r, itemId, itemText: listed ? itemLabel(listed.itemCode, listed.itemName) : '', itemName: listed?.itemName || '',
      itemType: listed?.itemType || '', uomId: '', uomCode: '', factorToBase: 1, baseUomCode: '',
      ...(listed?.itemType === 'NON_STOCK' || !itemId ? { stockLocationId: '', locationText: '' } : {}),
    }));
    if (!itemId) return;
    let item = itemDetailsRef.current[itemId];
    if (!item) {
      try {
        item = await itemService.getById(itemId);
        const fetched = item;
        setItemDetails(prev => ({ ...prev, [itemId]: fetched }));
      } catch (err: any) {
        notificationService.toast.error(err?.response?.data?.message || "Could not load the item's packs.");
        return;
      }
    }
    const it = item;
    const pack = defaultPack(it, side);
    const isStock = it.itemType === 'STOCK';
    const defLocId = isStock ? (it.entitySetting?.defaultStockLocationId || '') : '';
    const defLocText = itemLabel(it.entitySetting?.defaultStockLocationCode, it.entitySetting?.defaultStockLocationName);
    setRows(rows => rows.map(r => {
      if (r.key !== key || r.itemId !== itemId) return r; // the row moved on meanwhile
      return {
        ...r, itemText: itemLabel(it.itemCode, it.itemName), itemName: it.itemName, itemType: it.itemType,
        uomId: pack?.uomId || '', uomCode: pack?.uomCode || '', factorToBase: pack?.factorToBase || 1, baseUomCode: it.baseUomCode || '',
        ...(!isStock ? { stockLocationId: '', locationText: '' }
          : !r.stockLocationId && defLocId ? { stockLocationId: defLocId, locationText: defLocText } : {}),
      };
    }));
  }, [side]);

  /** The item's packs, base unit first; before the read lands, just the row's own pack. */
  const packOptions = useCallback((row: ItemFields): SelectOption[] => {
    const item = row.itemId ? itemDetails[row.itemId] : undefined;
    if (item) {
      return item.uoms.slice()
        .sort((a, b) => (Number(b.isBase) - Number(a.isBase)) || (a.displayOrder - b.displayOrder))
        .map(u => ({ value: u.uomId, label: packLabel(u) }));
    }
    return row.uomId ? [{ value: row.uomId, label: packLabel({ uomCode: row.uomCode, factorToBase: row.factorToBase }) }] : [];
  }, [itemDetails]);
  const packPatch = useCallback((row: ItemFields, uomId: string): Partial<ItemFields> => {
    const pack = itemDetails[row.itemId]?.uoms.find(u => u.uomId === uomId);
    return { uomId, uomCode: pack?.uomCode || '', factorToBase: pack?.factorToBase || 1 };
  }, [itemDetails]);
  const itemTypeOf = useCallback((row: ItemFields): ItemType | '' =>
    (row.itemId && itemDetails[row.itemId]?.itemType) || row.itemType, [itemDetails]);
  const baseUomOf = useCallback((row: ItemFields) =>
    row.baseUomCode || (row.itemId && itemDetails[row.itemId]?.baseUomCode) || '', [itemDetails]);

  return { loadItems, loadLocations, loadItemDetails, pickItem, packOptions, packPatch, itemTypeOf, baseUomOf };
};
export type ItemLineLookups = ReturnType<typeof useItemLineLookups>;

/** Where an item's ledger came from: "item override", "category override: X" or "default". */
export const ledgerSourceTag = (d: DocumentLineLedgerDto): string =>
  d.source === 'ITEM' ? 'item override'
  : d.source === 'CATEGORY' ? `category override${d.sourceCategoryName ? `: ${d.sourceCategoryName}` : ''}`
  : d.source === 'DEFAULT' ? 'default'
  : '';
const ledgerText = (d: DocumentLineLedgerDto) => [d.ledgerCode, d.ledgerName].filter(Boolean).join(' ');
/**
 * The ledger an ITEM line's own picker shows: none for a goods ledger (fixed — the line sends no
 * ledger); otherwise the user's pick, else the resolved pre-fill.
 */
export const effectiveItemLedgerId = (pickedId: string, info?: DocumentLineLedgerDto): string =>
  info?.isGoodsLedger ? '' : (pickedId || info?.ledgerId || '');
/** Seed label for the pre-filled ledger, in the pickers' "CODE - Name" form. */
export const prefillLedgerLabel = (info?: DocumentLineLedgerDto): string =>
  info?.ledgerId ? `${info.ledgerCode || ''} - ${info.ledgerName || ''}` : '';

/**
 * A ledger shown read-only (it follows the masters): "CODE Name" with where it came from; while
 * the read is in flight, "Resolving…"; when nothing resolved, `missingText` in red.
 * `ledger` undefined = not read yet; null = read, nothing resolved.
 */
export const ReadOnlyLedgerValue: React.FC<{
  ledger: { ledgerCode?: string | null; ledgerName?: string | null; tag?: string } | null | undefined;
  missingText: string;
  /** Tooltip of the resolved ledger. */
  title?: string;
}> = ({ ledger, missingText, title }) => {
  if (ledger === undefined) return <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', padding: '0.3rem 0' }}>Resolving…</div>;
  if (ledger === null) return <div className="text-danger" style={{ fontSize: '0.72rem', marginTop: '0.15rem' }}>{missingText}</div>;
  return (<>
    <div title={title}
      style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem', border: '1px solid var(--border-color)', borderRadius: 4, background: 'var(--bg-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {[ledger.ledgerCode, ledger.ledgerName].filter(Boolean).join(' ') || '—'}
    </div>
    {ledger.tag && <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>{ledger.tag}</div>}
  </>);
};

/** The Item cell of an ITEM line: the picker, or (locked to a source document line) the item read-only. */
export const ItemPickerCell: React.FC<{
  row: ItemFields; lookups: ItemLineLookups; onPick: (itemId: string) => void; locked?: boolean; error?: string;
  /** Tooltip of the locked item. */
  lockedTitle?: string;
}> = ({ row, lookups, onPick, locked, error, lockedTitle = "The bill line's item — returned goods keep it" }) => locked
  ? <div style={{ padding: '0.35rem 0.25rem', fontSize: '0.8125rem' }} title={lockedTitle}>{row.itemText || '—'}</div>
  : <SyutSelect isAsync loadOptions={lookups.loadItems} defaultOptions cacheOptions={false}
      seedOptions={row.itemId ? [{ value: row.itemId, label: row.itemText || '' }] : undefined}
      value={row.itemId} onChange={v => onPick((v as string) || '')}
      isClearable size="sm" menuPortalTarget={document.body} placeholder="Pick an item" error={error} />;

/**
 * The ITEM line's detail strip: pack, location (stock items), the ledger and the base quantity.
 * The ledger is what the line-ledgers read says: a goods ledger (stock item under periodic /
 * perpetual inventory) shows read-only with where it came from; any other item's ledger is the
 * editable `ledger` picker, pre-filled with the resolved ledger.
 */
export const ItemLineDetails: React.FC<{
  row: ItemFields & { quantity: string };
  lookups: ItemLineLookups;
  onChange: (patch: Partial<ItemFields>) => void;
  ledger: React.ReactNode;
  /** The item's resolved ledger (undefined until read / no item). */
  ledgerInfo?: DocumentLineLedgerDto;
  /** The ledger the user picked on the line ('' = none — the pre-fill applies). */
  pickedLedgerId?: string;
  errors?: { uom?: string; location?: string };
  packLocked?: boolean;
  /** Label of the editable ledger picker (default "Ledger (optional)"). */
  ledgerLabel?: string;
  /** What to say when the line-ledgers read found no ledger for the purpose (default: "No X ledger set — pick one"). */
  missingLedgerText?: string;
  /** False hides the location picker (e.g. a credit-note price allowance — nothing moves). */
  showLocation?: boolean;
  /** Under the location picker — e.g. the on-hand hint. */
  locationHint?: React.ReactNode;
  /** First in the strip — e.g. the credit note's "Goods returned" switch. */
  leading?: React.ReactNode;
}> = ({ row, lookups, onChange, ledger, ledgerInfo, pickedLedgerId, errors, packLocked, ledgerLabel = 'Ledger (optional)', missingLedgerText, showLocation = true, locationHint, leading }) => {
  const itemType = lookups.itemTypeOf(row);
  const baseQty = (parseFloat(row.quantity) || 0) * (row.factorToBase || 1);
  const baseCode = lookups.baseUomOf(row);
  const lbl: React.CSSProperties = { fontSize: '0.68rem', marginBottom: '0.1rem' };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.75rem', alignItems: 'start', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.4rem 0.6rem' }}>
      {leading}
      <div style={{ width: 170 }}>
        <label className="form-label" style={lbl}>Pack</label>
        <SyutSelect options={lookups.packOptions(row)} value={row.uomId} onChange={v => onChange(lookups.packPatch(row, (v as string) || ''))}
          placeholder={row.itemId ? 'Base unit' : 'Pick item first'} isDisabled={!row.itemId || packLocked}
          isClearable={false} size="sm" menuPortalTarget={document.body} error={errors?.uom} />
      </div>
      {showLocation && itemType !== 'NON_STOCK' && (
        <div style={{ width: 230 }}>
          <label className="form-label" style={lbl}>Location {itemType === 'STOCK' && <span className="text-danger">*</span>}</label>
          <SyutSelect isAsync loadOptions={lookups.loadLocations} defaultOptions cacheOptions={false}
            seedOptions={row.stockLocationId ? [{ value: row.stockLocationId, label: row.locationText || '' }] : undefined}
            value={row.stockLocationId} onChange={v => onChange({ stockLocationId: (v as string) || '' })}
            isClearable size="sm" menuPortalTarget={document.body} placeholder="Item's default location" error={errors?.location} />
          {locationHint}
        </div>
      )}
      <div style={{ width: 260 }}>
        {ledgerInfo?.isGoodsLedger ? (<>
          <label className="form-label" style={lbl}>Ledger</label>
          {ledgerInfo.ledgerId ? (<>
            <div title="Stock items post to the inventory ledger — set under Inventory → Inventory Settings"
              style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem', border: '1px solid var(--border-color)', borderRadius: 4, background: 'var(--bg-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ledgerText(ledgerInfo)}
            </div>
            <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>{ledgerSourceTag(ledgerInfo)}</div>
          </>) : (
            <div style={{ fontSize: '0.72rem', color: '#d97706', marginTop: '0.15rem' }}>
              ⚠ {ledgerInfo.purposeName || ledgerInfo.purposeCode} ledger not set — Inventory → Inventory Settings → Default ledgers
            </div>
          )}
        </>) : (<>
          <label className="form-label" style={lbl}>{ledgerLabel}</label>
          {ledger}
          {ledgerInfo && (
            <div style={{ fontSize: '0.66rem', color: ledgerInfo.ledgerId || !missingLedgerText ? 'var(--text-muted)' : '#d97706', marginTop: '0.15rem' }}>
              {ledgerInfo.ledgerId
                ? (pickedLedgerId && pickedLedgerId !== ledgerInfo.ledgerId
                    ? `Suggested: ${ledgerText(ledgerInfo)} · ${ledgerSourceTag(ledgerInfo)}`
                    : `Pre-filled · ${ledgerSourceTag(ledgerInfo)}`)
                : (missingLedgerText || `No ${ledgerInfo.purposeName || ledgerInfo.purposeCode} ledger set — pick one`)}
            </div>
          )}
        </>)}
      </div>
      {row.itemId && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', alignSelf: 'center' }}>
          = {qtyFmt(baseQty)}{baseCode ? ` ${baseCode}` : ''} <span style={{ color: 'var(--text-muted)' }}>(base)</span>
        </div>
      )}
    </div>
  );
};

/** Read view of an ITEM line: item, qty + pack, location, and (stock lines) what it adds to stock. */
export const ItemLineSummary: React.FC<{
  l: {
    itemCode?: string | null; itemName?: string | null; quantity?: number | null; uomCode?: string | null;
    locationCode?: string | null; locationName?: string | null; stockLocationId?: string | null;
    stockValue?: number | null; chargeShare?: number | null; taxShare?: number | null;
  };
  stockLabel?: string;
}> = ({ l, stockLabel = 'Stock value' }) => (
  <>
    <div>{itemLabel(l.itemCode, l.itemName) || '—'}</div>
    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
      {qtyFmt(l.quantity ?? 0)} {l.uomCode || ''}{l.locationCode || l.locationName ? ` · ${itemLabel(l.locationCode, l.locationName)}` : ''}
    </div>
    {(l.stockLocationId || (l.stockValue ?? 0) !== 0) && l.stockValue != null && (
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
        {stockLabel} (base) {money(l.stockValue)}
        {(l.chargeShare ?? 0) !== 0 && <> · incl. charges {money(l.chargeShare!)}</>}
        {(l.taxShare ?? 0) !== 0 && <> · incl. tax {money(l.taxShare!)}</>}
      </div>
    )}
  </>
);
